import { CODES, type Severity as CanonicalSeverity } from '@fragments-sdk/core';

/**
 * Shared rule-id / severity mappings for the governance pipeline.
 *
 * Convex stores rule state under snake_case keys (validator-safe);
 * the CLI + UI use slash-separated rule ids. This file is the single
 * source of truth for that translation and the default rule state.
 *
 * Consumers:
 *   - src/routes/api/govern/policy.ts (CLI-facing policy endpoint)
 *   - src/page/governance/components/GovernanceRules.tsx (policy editor UI)
 */

export type RuleId =
  | 'tokens/hardcoded-values'
  | 'components/usage'
  | 'a11y/wcag'
  | 'components/prefer-library'
  | 'tailwind/arbitrary-color'
  | 'tailwind/arbitrary-spacing'
  | 'tailwind/forbidden-palette'
  | 'tailwind/off-scale-spacing-token'
  | 'tailwind/raw-color-via-token'
  | 'tailwind/unknown-class';

/**
 * Rules the Governance UI editor offers as toggleable. `components/prefer-library`
 * is auto-managed via `canonicalSources` and must not appear in the editor;
 * customers disable it by removing all canonical sources.
 */
export type EditableRuleId = Exclude<RuleId, 'components/prefer-library'>;

export type ConvexRuleKey =
  | 'tokens_hardcoded_values'
  | 'components_usage'
  | 'a11y_wcag'
  | 'components_prefer_library'
  | 'tailwind_arbitrary_color'
  | 'tailwind_arbitrary_spacing'
  | 'tailwind_forbidden_palette'
  | 'tailwind_off_scale_spacing_token'
  | 'tailwind_raw_color_via_token'
  | 'tailwind_unknown_class';

export type Severity = 'error' | 'warning' | 'info';

export interface RuleState {
  enabled: boolean;
  severity: Severity;
  options?: Record<string, unknown>;
}

export type CodePolicyState = Record<string, RuleState>;

export const RULE_TO_CONVEX: Record<RuleId, ConvexRuleKey> = {
  'tokens/hardcoded-values': 'tokens_hardcoded_values',
  'components/usage': 'components_usage',
  'a11y/wcag': 'a11y_wcag',
  'components/prefer-library': 'components_prefer_library',
  'tailwind/arbitrary-color': 'tailwind_arbitrary_color',
  'tailwind/arbitrary-spacing': 'tailwind_arbitrary_spacing',
  'tailwind/forbidden-palette': 'tailwind_forbidden_palette',
  'tailwind/off-scale-spacing-token': 'tailwind_off_scale_spacing_token',
  'tailwind/raw-color-via-token': 'tailwind_raw_color_via_token',
  'tailwind/unknown-class': 'tailwind_unknown_class',
};

export const CONVEX_TO_RULE: Record<ConvexRuleKey, RuleId> = {
  tokens_hardcoded_values: 'tokens/hardcoded-values',
  components_usage: 'components/usage',
  a11y_wcag: 'a11y/wcag',
  components_prefer_library: 'components/prefer-library',
  tailwind_arbitrary_color: 'tailwind/arbitrary-color',
  tailwind_arbitrary_spacing: 'tailwind/arbitrary-spacing',
  tailwind_forbidden_palette: 'tailwind/forbidden-palette',
  tailwind_off_scale_spacing_token: 'tailwind/off-scale-spacing-token',
  tailwind_raw_color_via_token: 'tailwind/raw-color-via-token',
  tailwind_unknown_class: 'tailwind/unknown-class',
};

export const DEFAULT_RULE_STATES: Record<EditableRuleId, RuleState> = {
  'tokens/hardcoded-values': { enabled: true, severity: 'warning' },
  'components/usage': { enabled: false, severity: 'warning' },
  'a11y/wcag': { enabled: true, severity: 'warning' },
  'tailwind/arbitrary-color': { enabled: true, severity: 'warning' },
  'tailwind/arbitrary-spacing': { enabled: true, severity: 'warning' },
  'tailwind/forbidden-palette': { enabled: true, severity: 'warning' },
  'tailwind/off-scale-spacing-token': { enabled: true, severity: 'warning' },
  'tailwind/raw-color-via-token': { enabled: true, severity: 'warning' },
  'tailwind/unknown-class': { enabled: false, severity: 'info' },
};

export const CODE_TO_RULE = Object.fromEntries(
  CODES.map((entry) => [entry.code, entry.ruleId]),
) as Record<string, RuleId | string>;

export const RULE_TO_CODES = CODES.reduce<Record<string, string[]>>(
  (acc, entry) => {
    acc[entry.ruleId] = [...(acc[entry.ruleId] ?? []), entry.code];
    return acc;
  },
  {},
);

export const DEFAULT_CODE_STATES: CodePolicyState = Object.fromEntries(
  CODES.map((entry) => [
    entry.code,
    {
      enabled: entry.ruleId === 'tailwind/unknown-class' ? false : true,
      severity: severityFromCode(entry.defaultSeverity),
    },
  ]),
);

export function severityFromCode(severity: CanonicalSeverity): Severity {
  if (severity === 'critical' || severity === 'serious') return 'error';
  if (severity === 'moderate') return 'warning';
  return 'info';
}
